import React from 'react';
import {
  ShieldCheck, Zap, RefreshCw, Lock, BadgeCheck,
  HeartHandshake, CalendarCheck, Wallet, LifeBuoy,
} from 'lucide-react';

/**
 * ConversionUSPBar — modernes Trust/USP-Modul für konversionsstarke Touchpoints.
 *
 * Kein „AI-Slop": klare Icons, cleane Copy, konsequent gerundete Pills,
 * Emerald-Akzent, sanfte Micro-Interaction.
 *
 * Variants:
 *   • strip      — full-width 4-Spalten-Grid (Cart, Warenkorb, Checkout unten)
 *   • pills      — kompakte Pill-Reihe (Configurator, Cart Drawer über CTA)
 *   • valuestack — großes Value-Stack-Modul (über CTA-Buttons, Hero)
 *   • dark       — dunkle Variante für dunkle Sektionen
 */

const CORE_USPS = [
  {
    icon: Zap,
    label: 'Sofort digital aktiv',
    hint: 'Card in 60 Sek. im Dashboard — kein Versand',
  },
  {
    icon: RefreshCw,
    label: '14 Tage Widerrufsrecht',
    hint: 'Volle Rückerstattung, keine Diskussion',
  },
  {
    icon: BadgeCheck,
    label: 'Faire Kündigung',
    hint: '30 Tage Frist zum Laufzeitende — formlos im Dashboard',
  },
  {
    icon: Lock,
    label: 'Sichere Zahlung · Stripe',
    hint: 'TLS · 3-D-Secure · PCI-DSS Level 1',
  },
];

const VALUE_STACK = [
  { icon: HeartHandshake, label: 'Nie wieder allein bei Autoproblemen', hint: 'Warnleuchte, seltsames Geräusch, Zweifel an der Werkstatt? Ein Experte schaut drauf, erklärt verständlich und sagt dir, was wirklich nötig ist' },
  { icon: CalendarCheck,  label: 'Wir übernehmen die Organisation', hint: 'TÜV-Fristen, Werkstatttermine, Angebote, Papierkram — FuelX kümmert sich im Hintergrund, du triffst nur noch die Entscheidung' },
  { icon: Wallet,         label: 'Kostenkontrolle statt böser Überraschungen', hint: 'Du siehst jederzeit, was dein Auto kostet und wo du sparst — Rechnungen geprüft, alles transparent in deinem Dashboard' },
  { icon: LifeBuoy,       label: 'Deine Plattform für alles rund ums Auto', hint: 'Sorge, Problem oder Frage? Einfach im Dashboard melden — FuelX-Experten helfen sofort, organisieren alles und bleiben dran, bis es gelöst ist' },
];

/**
 * strip — Full-width 4-column trust strip.
 * Ideal: unter dem Total-Preis, direkt über dem CTA-Button (Checkout, Warenkorb, Cart Drawer).
 */
export default function ConversionUSPBar({ variant = 'strip', dark = false, compact = false, testId = 'conversion-usp' }) {
  if (variant === 'valuestack') {
    return <ValueStack dark={dark} compact={compact} testId={testId} />;
  }
  if (variant === 'pills') {
    return <Pills dark={dark} testId={testId} />;
  }
  return <Strip dark={dark} testId={testId} />;
}

/* ═══════════════════════════════════════════════════════════════
 * VARIANT: STRIP — full-width 4-column grid
 * ═══════════════════════════════════════════════════════════════ */
function Strip({ dark, testId }) {
  const bg = dark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-stone-200';
  const bgHover = dark ? 'hover:bg-white/[0.06] hover:border-emerald-400/40' : 'hover:border-stone-900 hover:shadow-md';
  const chip = dark ? 'bg-emerald-500/15 border-emerald-400/40' : 'bg-emerald-50 border-emerald-100';
  const iconClr = dark ? 'text-emerald-300' : 'text-emerald-700';
  const label = dark ? 'text-white' : 'text-stone-900';
  const hint = dark ? 'text-stone-400' : 'text-stone-500';

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3" data-testid={testId}>
      {CORE_USPS.map((u) => {
        const Icon = u.icon;
        return (
          <div
            key={u.label}
            className={`group ${bg} border ${bgHover} rounded-2xl p-3.5 sm:p-4 transition-all duration-300`}
            data-testid={`${testId}-${u.label.split(' ')[0].toLowerCase()}`}
          >
            <div className={`w-9 h-9 rounded-xl ${chip} border flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform duration-300`}>
              <Icon className={`w-4 h-4 ${iconClr}`} strokeWidth={2.2} />
            </div>
            <p className={`text-[13px] sm:text-sm font-semibold ${label} leading-tight tracking-[-0.005em]`}>
              {u.label}
            </p>
            <p className={`text-[11px] ${hint} leading-snug mt-1`}>
              {u.hint}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * VARIANT: PILLS — kompakte Pill-Reihe (klein, wickelt schön)
 * Ideal: direkt über einem CTA-Button, im schmalen Cart Drawer.
 * ═══════════════════════════════════════════════════════════════ */
function Pills({ dark, testId }) {
  const pill = dark
    ? 'bg-white/[0.06] border-white/10 text-stone-200 hover:border-emerald-400/50'
    : 'bg-stone-50 border-stone-200 text-stone-700 hover:border-stone-900 hover:bg-white';
  const iconClr = dark ? 'text-emerald-300' : 'text-emerald-600';

  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {CORE_USPS.map((u) => {
        const Icon = u.icon;
        return (
          <div
            key={u.label}
            className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${pill}`}
            data-testid={`${testId}-pill-${u.label.split(' ')[0].toLowerCase()}`}
          >
            <Icon className={`w-3 h-3 ${iconClr} flex-shrink-0`} strokeWidth={2.4} />
            {u.label}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * VARIANT: VALUE STACK — großes „Was du bekommst"-Modul
 * Ideal: über CTAs in Cart Drawer / Warenkorb / Checkout — verdeutlicht den Deal.
 * ═══════════════════════════════════════════════════════════════ */
function ValueStack({ dark, compact, testId }) {
  const container = dark
    ? 'bg-stone-950 border-white/10 text-white'
    : 'bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 border-emerald-100';
  const chip = dark ? 'bg-emerald-500/15 border-emerald-400/40' : 'bg-white border-emerald-200';
  const iconClr = dark ? 'text-emerald-300' : 'text-emerald-700';
  const label = dark ? 'text-white' : 'text-stone-900';
  const hint = dark ? 'text-stone-400' : 'text-stone-500';
  const eyebrow = dark ? 'text-emerald-300' : 'text-emerald-700';

  if (compact) {
    return (
      <div className={`rounded-2xl border overflow-hidden ${dark ? 'bg-stone-950 border-white/10' : 'bg-white border-stone-200'}`} data-testid={testId}>
        <div className={`px-4 py-3 flex items-center gap-2 border-b ${dark ? 'border-white/10 bg-white/[0.03]' : 'border-stone-100 bg-emerald-50/50'}`}>
          <ShieldCheck className={`w-3.5 h-3.5 ${iconClr}`} strokeWidth={2.2} />
          <p className={`text-[9.5px] font-semibold uppercase tracking-[0.2em] ${eyebrow}`}>
            Was in deiner Card steckt
          </p>
        </div>
        <div className={`divide-y ${dark ? 'divide-white/5' : 'divide-stone-100'}`}>
          {VALUE_STACK.map((u) => {
            const Icon = u.icon;
            return (
              <div key={u.label} className="flex items-start gap-3 px-4 py-3" data-testid={`${testId}-item-${u.label.split(' ')[0].toLowerCase()}`}>
                <div className={`w-8 h-8 rounded-lg ${chip} border flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <Icon className={`w-4 h-4 ${iconClr}`} strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-[13px] font-semibold ${label} leading-tight tracking-[-0.005em]`}>{u.label}</p>
                  <p className={`text-[11px] ${hint} leading-snug mt-1`}>{u.hint}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border ${container} p-4 sm:p-6 relative overflow-hidden`} data-testid={testId}>
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-300/15 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className={`w-4 h-4 ${iconClr}`} strokeWidth={2.2} />
          <p className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${eyebrow}`}>
            Was in deiner Card steckt
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
          {VALUE_STACK.map((u) => {
            const Icon = u.icon;
            return (
              <div key={u.label} className="flex items-start gap-3" data-testid={`${testId}-item-${u.label.split(' ')[0].toLowerCase()}`}>
                <div className={`w-9 h-9 rounded-full ${chip} border flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4 h-4 ${iconClr}`} strokeWidth={2.2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold ${label} leading-tight tracking-[-0.005em]`}>{u.label}</p>
                  <p className={`text-[12px] sm:text-[11px] ${hint} leading-snug mt-0.5`}>{u.hint}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
